import type {
  BatchJobFilter,
  BatchJobSummary
} from '@/hooks/useWorkflowBatchJobs';

const PERCENT = 100;

// `mode` is the raw selection discriminator from batch_job. Neither "filter" nor
// "explicit" means anything to a user, so it is never rendered directly.
//
// A `migration` job is only ever visible to a superuser (it carries no
// created_by, so the list's owner narrowing hides it from everyone else), but it
// still reaches this list and needs a label a person can read.
const MODE_LABELS: Record<string, string> = {
  filter: 'Via filters',
  explicit: 'Manually selected',
  migration: 'Trace migration'
};

export function modeLabel(mode: string): string {
  return MODE_LABELS[mode] ?? mode;
}

// One message per filter: "nothing has ever run here" is wrong when the reader
// has simply narrowed to Running and nothing is in flight.
const EMPTY_BY_FILTER: Record<BatchJobFilter, string> = {
  all: 'No batch action has been run on this workflow yet.',
  running: 'No batch action is running on this workflow.',
  completed: 'No batch action has completed on this workflow yet.',
  failures: 'No batch action has failed on this workflow.'
};

export function emptyStateMessage(
  filter: BatchJobFilter,
  runBy?: string
): string {
  const base = EMPTY_BY_FILTER[filter];
  if (!runBy) {
    return base;
  }
  // Drop the trailing stop so the attribution reads as part of the sentence.
  return `${base.replace(/\.$/, '')} for ${runBy}.`;
}

export type OutcomeChip = { key: string; label: string; tone: 'ok' | 'bad' };

/**
 * What a finished job actually did, as chips. A single "Failed" badge is a lie
 * whenever only some items failed, so a partial outcome shows both counts side
 * by side and lets the reader see the ratio.
 *
 * Returns [] while the job is still working — the caller shows its live status
 * instead.
 */
export function outcomeChips(
  job: Pick<BatchJobSummary, 'status' | 'succeeded' | 'failed'>,
  isTerminal: boolean
): OutcomeChip[] {
  if (!isTerminal) {
    return [];
  }
  const chips: OutcomeChip[] = [];
  if (job.succeeded > 0) {
    chips.push({
      key: 'completed',
      label: `${job.succeeded} completed`,
      tone: 'ok'
    });
  }
  if (job.failed > 0) {
    chips.push({ key: 'failed', label: `${job.failed} failed`, tone: 'bad' });
  }
  return chips;
}

const SECOND_MS = 1000;
const MINUTE_S = 60;
const HOUR_MIN = 60;

/**
 * Compact elapsed time: `16s`, `4m 12s`, `1h 20m`. Coarser as it grows, because
 * nobody reads the seconds of an hour-long batch.
 */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / SECOND_MS));
  const seconds = totalSeconds % MINUTE_S;
  const totalMinutes = Math.floor(totalSeconds / MINUTE_S);
  const minutes = totalMinutes % HOUR_MIN;
  const hours = Math.floor(totalMinutes / HOUR_MIN);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (totalMinutes > 0) {
    return `${totalMinutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export type ProgressSegments = {
  succeededPercent: number;
  failedPercent: number;
  processedPercent: number;
};

/**
 * Progress split into a succeeded and a failed share of the whole job, so a run
 * with 7 failures out of 1240 reads as a sliver of red rather than a fully red
 * bar. Percentages are of `total`, and are floored so they never sum past 100.
 */
export function progressSegments(
  job: Pick<BatchJobSummary, 'total' | 'processed' | 'succeeded' | 'failed'>
): ProgressSegments {
  if (job.total <= 0) {
    return { succeededPercent: 0, failedPercent: 0, processedPercent: 0 };
  }
  const share = (n: number) =>
    Math.min(PERCENT, Math.max(0, (n / job.total) * PERCENT));

  const failedPercent = share(job.failed);
  // Derived from succeeded rather than processed - failed: the two counters are
  // written by different activities and can disagree mid-flight.
  const succeededPercent = Math.min(
    share(job.succeeded),
    PERCENT - failedPercent
  );

  return {
    succeededPercent,
    failedPercent,
    processedPercent: Math.round(share(job.processed))
  };
}
